import React, { useRef, useState } from 'react'
import logo from '../Images/logo-main-black.png'
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { Link, useNavigate } from "react-router-dom";
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert'
import swal from 'sweetalert';

// Firebase
import { db } from '../firebase';
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { collection, query, getDocs, where, doc, setDoc, getDoc, Timestamp } from "firebase/firestore";
import { recordAdminLogin } from '../utils/adminPresence';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faEye, faEyeSlash, faLock } from '@fortawesome/free-solid-svg-icons'

function Login() {
    let navigate = useNavigate();
    const emailRef = useRef();
    const passwordRef = useRef();
    const [showAlert, setShowAlert] = useState(false);
    const [showProgress, setShowProgress] = useState(false);
    const [showSignInbtn, setShowSignInbtn] = useState(true);
    const [therrorMessage, setErrorMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    async function findAdminRecord(userID, email) {
        const directDoc = await getDoc(doc(db, "Admins", userID));
        if (directDoc.exists()) {
            return { id: directDoc.id, ...directDoc.data() };
        }

        const byUserId = await getDocs(query(collection(db, "Admins"), where("userID", "==", userID)));
        if (!byUserId.empty) {
            const foundDoc = byUserId.docs[0];
            return { id: foundDoc.id, ...foundDoc.data() };
        }

        if (email) {
            const byEmail = await getDocs(query(collection(db, "Admins"), where("userEmail", "==", email)));
            if (!byEmail.empty) {
                const foundDoc = byEmail.docs[0];
                return { id: foundDoc.id, ...foundDoc.data() };
            }
        }

        return null;
    }

    function getReadableAuthError(error) {
        if (!error) {
            return 'Something went wrong while signing you in.';
        }

        if (typeof error.message === 'string' && error.message.trim()) {
            return error.message.replace(/^Firebase:\s*/i, '').trim();
        }

        return 'Something went wrong while signing you in.';
    }

    async function SignInUser() {
        const email = emailRef.current.value;
        const password = passwordRef.current.value;
        const auth = getAuth();

        setShowProgress(true);
        setShowAlert(false);
        setShowSignInbtn(false);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const loggedInUser = userCredential.user;
            const adminRecord = await findAdminRecord(loggedInUser.uid, loggedInUser.email);

            if (!adminRecord) {
                await signOut(auth);
                setShowProgress(false);
                setShowSignInbtn(true);
                swal('Access denied', 'This account is not registered as an admin.', 'error');
                return;
            }

            if (String(adminRecord.accessStatus || 'active').toLowerCase() === 'disabled') {
                await signOut(auth);
                setShowProgress(false);
                setShowSignInbtn(true);
                swal('Access denied', 'This admin account has been disabled.', 'error');
                return;
            }

            const phoneNumber = adminRecord.phone || adminRecord.phoneNumber;
            const userName = adminRecord.userName || loggedInUser.displayName || loggedInUser.email;

            if (!phoneNumber) {
                await signOut(auth);
                setShowProgress(false);
                setShowSignInbtn(true);
                swal('Missing phone number', 'This admin record does not have a phone number for OTP verification.', 'error');
                return;
            }

            try {
                await processOtp(phoneNumber, userName);
            } catch (otpError) {
                console.error('OTP flow failed:', otpError);
                await signOut(auth);
                setShowProgress(false);
                setShowSignInbtn(true);
                swal('OTP failed', 'We could not send the verification code for this account.', 'error');
            }
        } catch (error) {
            setShowProgress(false);
            setShowSignInbtn(true);
            swal('Login failed', getReadableAuthError(error), 'error');
        }
    }

    function generateOTP(length = 6) {
        return Math.floor(100000 + Math.random() * 900000).toString().substring(0, length);
    }

    function createOtpPromptContent(phoneNumber) {
        const wrapper = document.createElement('div');
        wrapper.className = 'otpPromptContent';

        const eyebrow = document.createElement('span');
        eyebrow.className = 'otpPromptEyebrow';
        eyebrow.textContent = 'Two-step verification';

        const title = document.createElement('h4');
        title.className = 'otpPromptTitle';
        title.textContent = 'Enter your OTP code';

        const description = document.createElement('p');
        description.className = 'otpPromptDescription';
        description.textContent = `We sent a 6-digit verification code to ${phoneNumber}.`;

        const input = document.createElement('input');
        input.className = 'otpPromptInput';
        input.placeholder = 'Enter 6-digit code';
        input.maxLength = 6;
        input.autocomplete = 'one-time-code';
        input.inputMode = 'numeric';

        wrapper.appendChild(eyebrow);
        wrapper.appendChild(title);
        wrapper.appendChild(description);
        wrapper.appendChild(input);

        return wrapper;
    }

    async function processOtp(phoneNumber, userName) {
        const otpCode = generateOTP();
        const expiresAt = Timestamp.fromDate(new Date(Date.now() + 20 * 60 * 1000)); // 20 minutes

        const otpDocRef = doc(db, 'otps', phoneNumber);
        await setDoc(otpDocRef, {
            code: otpCode,
            phoneNumber,
            createdAt: Timestamp.now(),
            expiresAt,
            verified: false
        });

        const auth = getAuth();
        const user = auth.currentUser;
        const token = await user.getIdToken();


        const smsSent = await sendSMS(userName, phoneNumber, otpCode, token);
        if (!smsSent) {
            throw new Error('SMS delivery failed.');
        }
        swal("OTP Sent", `A verification code has been sent to ${phoneNumber}`, "info");

        promptForOtp(phoneNumber);
    }

    async function promptForOtp(phoneNumber) {
        const content = createOtpPromptContent(phoneNumber);
        swal({
            buttons: {
                cancel: {
                    text: 'Cancel',
                    visible: true,
                    className: 'otpModalSecondaryBtn'
                },
                confirm: {
                    text: 'Verify code',
                    closeModal: false,
                    className: 'otpModalPrimaryBtn'
                }
            },
            className: 'otpSweetAlert',
            closeOnClickOutside: false,
            closeOnEsc: false,
            content
        }).then(async (confirmed) => {
            if (!confirmed) {
                return;
            }

            const inputOtp = content.querySelector('.otpPromptInput')?.value?.trim() || '';
            if (!inputOtp) {
                return swal('Missing code', 'Please enter the verification code sent to your phone.', 'warning');
            }

            const docRef = doc(db, 'otps', phoneNumber);
            const otpSnap = await getDoc(docRef);

            if (!otpSnap.exists()) {
                return swal("Error", "OTP not found.", "error");
            }

            const otpData = otpSnap.data();
            const now = Timestamp.now();

            if (otpData.verified) {
                return swal("Used", "This OTP has already been used.", "warning");
            }

            if (now > otpData.expiresAt) {
                return swal("Expired", "Your OTP has expired.", "error");
            }

            if (otpData.code !== inputOtp) {
                return swal("Invalid", "Wrong OTP. Try again.", "error");
            }

            await setDoc(docRef, { ...otpData, verified: true }, { merge: true });
            swal("Success", "OTP verified successfully!", "success").then(async () => {
                const auth = getAuth();
                const verifiedUser = auth.currentUser;
                if (verifiedUser) {
                    try {
                        await recordAdminLogin({
                            userId: verifiedUser.uid,
                            email: verifiedUser.email,
                            path: '/dashboard'
                        });
                    } catch (presenceError) {
                        console.error('Failed to record admin login:', presenceError);
                    }
                }
                navigate("/dashboard");
            });
        });

        setTimeout(() => {
            content.querySelector('.otpPromptInput')?.focus();
        }, 0);
    }

    const sendSMS = async (userName, phoneNumber, verificationcode, token) => {
        const message = `Hi ${userName}, your verification code for Ubunifu College is ${verificationcode}. This code will expire in 20 minutes.`;
        const smsData = {
            phoneNumber: phoneNumber,
            message: message
        };

        try {
            const response = await fetch("https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`

                },
                body: JSON.stringify(smsData)
            });

            const data = await response.json();
            console.log(data);
            return true;
        } catch (error) {
            console.error("Error sending SMS: ", error);
            return false;
        }
    };

    return (
        <div className='d-flex loginContainer'>
            <div className='loginLeft'>
                <video src="https://res.cloudinary.com/ubunifu/video/upload/v1747845880/5683800-uhd_2160_3840_25fps_a0kaec.mp4" muted autoPlay loop></video>
                <div className="authMediaOverlay"></div>
                <div className="authMediaContent">
                    <span className="authBadge">Ubunifu Staff Portal</span>
                    <h2>Ubunifu Smart Campus</h2>
                </div>
            </div>
            <div className='loginRight'>
                <div className="authPanel">
                    <Link to="/"><img src={logo} alt="" className='loginLogo' /></Link>
                    <h5>Welcome back!</h5>
                    <h6>Enter your staff email address and password to access your account.</h6>

                    <Form
                        onSubmit={(event) => {
                            event.preventDefault();
                            SignInUser();
                        }}
                    >
                        <Form.Label className="inputLabel">Email</Form.Label>
                        <InputGroup className="mb-3 authInputGroup">
                            <InputGroup.Text id="login-email-addon">
                                <FontAwesomeIcon icon={faEnvelope} className="theLoginIcon" />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Enter your email"
                                type="email"
                                className='loginForm'
                                ref={emailRef}
                                autoComplete="email"
                            />
                        </InputGroup>

                        <Form.Label className="inputLabel">Password</Form.Label>
                        <InputGroup className="mb-2 authInputGroup">
                            <InputGroup.Text id="login-password-addon">
                                <FontAwesomeIcon icon={faLock} className="theLoginIcon" />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Enter your Password"
                                type={showPassword ? "text" : "password"}
                                className='loginForm'
                                ref={passwordRef}
                                autoComplete="current-password"
                            />
                            <InputGroup.Text className="passwordToggleAddon">
                                <button
                                    type="button"
                                    className="passwordToggleBtn"
                                    onClick={() => setShowPassword((current) => !current)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="theLoginIcon" />
                                </button>
                            </InputGroup.Text>
                        </InputGroup>

                        <div className="loginActionRow">
                            <Link to="/reset-password" className='fgPassLink'>
                                Forgot password?
                            </Link>
                        </div>

                        {showAlert &&
                            <Alert variant={'warning'} className="mt-3">
                                {therrorMessage}
                            </Alert>
                        }

                        {showSignInbtn &&
                            <button type="submit" className='signInbtnn'>
                                Sign In
                            </button>
                        }

                        {showProgress &&
                            <Button variant="primary" disabled className="signInbtProgress mt-3">
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                                <span> Logging in...</span>
                            </Button>
                        }
                    </Form>

                    <p className="authPanelNote">Protected with password and OTP verification for staff-only access.</p>
                </div>
            </div>
        </div>
    )
}

export default Login;
